import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaCheckCircle,
  FaComments,
  FaExternalLinkAlt,
  FaSpinner,
  FaTruck,
  FaExclamationTriangle,
} from 'react-icons/fa';
import { orderTrackingService } from '../services/apiService';
import { formatPrice } from '../utils/currency';
import DeliveryOTP from './orderTracking/DeliveryOTP';
import CancelOrderModal from './orderTracking/CancelOrderModal';
import ReassignmentOfferCard from './orderTracking/ReassignmentOfferCard';
import OrderChatPanel from './orderTracking/OrderChatPanel';
import {
  LINEAR_FLOW,
  STATE_LABELS,
  formatCountdown,
  showCarrierFromState,
  timelineNodeStatus,
  isTerminalState,
} from '../utils/orderTrackingUtils';

const POLL_MS = 30000;

function TimelineNode({ stateKey, status, label }) {
  const isCurrent = status === 'current';
  const isDone = status === 'done';
  return (
    <li className="relative flex gap-4 pb-6 last:pb-0">
      <div className="flex flex-col items-center">
        <span
          className={`relative z-10 flex h-4 w-4 shrink-0 rounded-full border-2 ${
            isCurrent
              ? 'border-amber-400 bg-amber-400 animate-pulse shadow-[0_0_12px_rgba(251,191,36,0.6)]'
              : isDone
              ? 'border-emerald-500 bg-emerald-500'
              : 'border-slate-600 bg-slate-800'
          }`}
        />
        <span className="absolute top-4 h-full w-px bg-slate-700 last:hidden" aria-hidden />
      </div>
      <div className="min-w-0 flex-1 pt-[-2px]">
        <p
          className={`text-sm font-medium ${
            isCurrent ? 'text-amber-300' : isDone ? 'text-slate-200' : 'text-slate-500'
          }`}
        >
          {label}
        </p>
      </div>
    </li>
  );
}

function ExpressBuyCountdown({ expiresAt }) {
  const [remaining, setRemaining] = useState(() =>
    expiresAt ? new Date(expiresAt).getTime() - Date.now() : 0
  );

  useEffect(() => {
    if (!expiresAt) return undefined;
    const id = setInterval(() => {
      setRemaining(new Date(expiresAt).getTime() - Date.now());
    }, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  if (!expiresAt || remaining <= 0) return null;
  const urgent = remaining < 5 * 60 * 1000;

  return (
    <div
      className={`mt-4 rounded-xl border px-4 py-3 text-sm ${
        urgent
          ? 'border-red-700/50 bg-red-950/30 text-red-200'
          : 'border-amber-700/40 bg-amber-950/25 text-amber-200'
      }`}
    >
      Seller must confirm in{' '}
      <strong className={urgent ? 'text-red-300' : ''}>{formatCountdown(remaining)}</strong>
    </div>
  );
}

/**
 * B-08 — Order Tracking (12 states)
 */
export default function OrderTracking() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const mockState = searchParams.get('mockState');

  const [order, setOrder] = useState(null);
  const [carrierEvents, setCarrierEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelFeeRequired, setCancelFeeRequired] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const prevStateRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await orderTrackingService.getOrder(orderId, mockState);
      const o = res?.order || res;
      if (prevStateRef.current && o?.state && prevStateRef.current !== o.state) {
        toast.info(`Order updated: ${STATE_LABELS[o.state] || o.state}`);
      }
      prevStateRef.current = o?.state || null;
      setOrder(o);

      if (showCarrierFromState(o?.state)) {
        const tracking = await orderTrackingService.getTrackingEvents(orderId);
        setCarrierEvents(tracking?.events || []);
      }
    } catch {
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [orderId, mockState]);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const handleCancel = async (reason, { payCancelFee = false } = {}) => {
    setActing(true);
    try {
      const res = await orderTrackingService.cancelOrder(orderId, reason, { payCancelFee });
      await load();
      setShowCancelModal(false);
      setCancelFeeRequired(false);
      toast.success(
        payCancelFee
          ? 'Order cancelled — refund minus ₹100 cancel fee'
          : 'Order cancelled — refund initiated if paid'
      );
    } catch (err) {
      if (err.code === 'CANCEL_FEE_REQUIRED') {
        setCancelFeeRequired(true);
        toast.warn(err.message || '₹100 cancel fee applies — dispatch photo already uploaded');
      } else {
        toast.error(err.message || 'Could not cancel order');
      }
    } finally {
      setActing(false);
    }
  };

  const handleInTransitCancel = async () => {
    if (!window.confirm('Request cancellation while in transit? We will try to intercept with the carrier.')) {
      return;
    }
    setActing(true);
    try {
      await orderTrackingService.cancelInTransit(orderId, 'Buyer requested in-transit cancellation');
      await load();
      toast.success('Cancellation processed');
    } catch (err) {
      toast.error(err.message || 'Carrier could not intercept — you must receive the part');
    } finally {
      setActing(false);
    }
  };

  const handleConfirmDelivery = async () => {
    toast.info('Enter the 4-digit code sent to your phone to confirm delivery.');
  };

  const handleRaiseClaim = () => {
    navigate('/returns-disputes', { state: { tab: 'raise', orderId } });
  };

  const handleMessageSeller = () => {
    if (!order.canUseOrderChat) {
      toast.info('Chat opens once the seller accepts your order.');
      return;
    }
    setShowChat(true);
  };

  const handleReportPackageContact = async () => {
    if (!window.confirm(
      'Report that the seller included phone or address in the package? ₹100 will be credited to your wallet if confirmed.'
    )) {
      return;
    }
    const notes = window.prompt('Optional details (what was included):', '') || '';
    setActing(true);
    try {
      const res = await orderTrackingService.reportPackageContact(orderId, notes);
      toast.success(res?.message || 'Report submitted — ₹100 credited to wallet');
      await load();
    } catch (err) {
      if (err.code === 'ALREADY_REPORTED') {
        toast.warn(err.message);
      } else {
        toast.error(err.message || 'Could not submit report');
      }
    } finally {
      setActing(false);
    }
  };

  if (loading && !order) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0b1120] text-slate-400">
        <FaSpinner className="animate-spin text-2xl" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#0b1120] px-4 py-16 text-center text-slate-100">
        <p className="text-lg font-semibold">Order not found</p>
        <button
          type="button"
          onClick={() => navigate('/myorder')}
          className="mt-6 rounded-xl bg-amber-500 px-6 py-2.5 font-bold text-slate-900"
        >
          My orders
        </button>
      </div>
    );
  }

  const currentState = order.state;
  const showCarrier = showCarrierFromState(currentState);
  const terminal = isTerminalState(currentState);

  const timelineStates = terminal
    ? [...LINEAR_FLOW, currentState]
    : LINEAR_FLOW;

  return (
    <div className="min-h-screen bg-[#0b1120] px-4 py-8 text-slate-100">
      <div className="mx-auto max-w-2xl">
        {location.state?.paymentSuccess && (
          <div className="mb-6 rounded-2xl border border-emerald-700/50 bg-emerald-950/30 p-4 text-center text-emerald-200">
            <FaCheckCircle className="mx-auto mb-2 text-2xl text-emerald-400" />
            Payment successful — tracking your order
          </div>
        )}

        <header className="rounded-2xl border border-slate-700/60 bg-slate-800/40 p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Order</p>
          <h1 className="mt-1 text-xl font-bold text-white">{order.partName}</h1>
          <p className="mt-2 font-mono text-sm text-amber-400">Order id : {order.orderId}</p>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-400">
            <span>Seller — {order.sellerCity}</span>
            <span>{formatPrice(order.total)}</span>
            {order.orderDate && (
              <span>{new Date(order.orderDate).toLocaleDateString('en-IN')}</span>
            )}
          </div>

          <div className="mt-4 flex items-start gap-3 rounded-xl border border-amber-700/30 bg-amber-950/20 px-4 py-3">
            <FaTruck className="mt-0.5 shrink-0 text-amber-400" />
            <div>
              <p className="text-sm font-semibold text-amber-200">
                {STATE_LABELS[currentState] || currentState}
              </p>
              {order.lastUpdatedAt && (
                <p className="mt-1 text-xs text-slate-500">
                  Updated {new Date(order.lastUpdatedAt).toLocaleString('en-IN')}
                </p>
              )}
            </div>
          </div>

          {order.isExpressBuy && currentState === 'PENDING' && (
            <ExpressBuyCountdown expiresAt={order.expressBuyExpiresAt} />
          )}

          {(order.reassignment || (order.reassignmentCount > 0 && currentState === 'PENDING')) && (
            <div className="mt-4 rounded-xl border border-blue-700/40 bg-blue-950/25 px-4 py-3 text-sm">
              <p className="text-blue-200 font-semibold mb-1">
                {currentState === 'PENDING' ? 'Reassigned — awaiting new seller' : 'Seller reassigned'}
              </p>
              {order.reassignment && (
                <p className="text-blue-200">
                  <span className="line-through text-slate-500">
                    {order.reassignment.originalSellerCity}
                  </span>
                  {' → '}
                  <strong className="text-white">{order.reassignment.newSellerCity}</strong>
                </p>
              )}
              <p className="mt-1 text-xs text-blue-300/80">
                Same price guaranteed. New seller has 2h to accept.
                {order.reassignmentCount > 1 && (
                  <span className="block mt-1">Reassignment {order.reassignmentCount} of 5</span>
                )}
              </p>
            </div>
          )}
        </header>

        <ReassignmentOfferCard orderId={orderId} onUpdated={load} />

        {(order.canEnterDeliveryOtp || currentState === 'OUT_FOR_DELIVERY' || order.showDeliveryOtp) &&
          !order.otpConfirmed && (
          <div className="mt-6">
            <DeliveryOTP
              orderId={orderId}
              mockState={mockState}
              onConfirmed={() => load()}
            />
          </div>
        )}

        {order.carrierSilentOver24h && showCarrier && (
          <div className="mt-4 flex gap-3 rounded-xl border border-amber-800/50 bg-amber-950/20 px-4 py-3 text-sm text-amber-200">
            <FaExclamationTriangle className="shrink-0 mt-0.5" />
            Tracking update delayed. Contact support if concerned.
          </div>
        )}

        {showCarrier && order.awb && (
          <section className="mt-6 rounded-2xl border border-slate-700/60 bg-slate-800/40 p-5">
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">
              Carrier tracking
            </h2>
            <p className="mt-2 text-white">
              {order.carrierName} · AWB <span className="font-mono">{order.awb}</span>
            </p>
            {order.lastScanEvent && (
              <p className="mt-2 text-sm text-slate-400">Last scan: {order.lastScanEvent}</p>
            )}
            {order.carrierLink && (
              <a
                href={order.carrierLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-amber-400 hover:text-amber-300"
              >
                Track on carrier site <FaExternalLinkAlt className="text-xs" />
              </a>
            )}
            {carrierEvents.length > 0 && (
              <ul className="mt-4 space-y-2 border-t border-slate-700/50 pt-4">
                {carrierEvents.slice(0, 5).map((ev, i) => (
                  <li key={i} className="text-xs text-slate-400">
                    {ev.description}
                    {ev.location ? ` — ${ev.location}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <section className="mt-6 rounded-2xl border border-slate-700/60 bg-slate-800/40 p-5">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">
            Progress timeline
          </h2>
          <ol className="relative mt-6 pl-1">
            {timelineStates.map((stateKey) => (
              <TimelineNode
                key={stateKey}
                stateKey={stateKey}
                status={timelineNodeStatus(stateKey, currentState)}
                label={STATE_LABELS[stateKey] || stateKey}
              />
            ))}
          </ol>
        </section>

        <section className="mt-6 space-y-3">
          {order.canCancel && (
            <button
              type="button"
              disabled={acting}
              onClick={() => {
                setCancelFeeRequired(false);
                setShowCancelModal(true);
              }}
              className="w-full rounded-xl border border-red-700/50 py-3 text-sm font-semibold text-red-300 hover:bg-red-950/30 disabled:opacity-50"
            >
              Cancel order
            </button>
          )}

          {currentState === 'IN_TRANSIT' && (
            <button
              type="button"
              disabled={acting}
              onClick={handleInTransitCancel}
              className="w-full rounded-xl border border-orange-700/50 py-3 text-sm font-semibold text-orange-300 hover:bg-orange-950/30 disabled:opacity-50"
            >
              Request in-transit cancellation
            </button>
          )}

          {order.awaitingDeliveryConfirmation && !order.canEnterDeliveryOtp && (
            <p className="rounded-xl border border-slate-600/60 bg-slate-800/50 px-4 py-3 text-center text-xs text-slate-400">
              Waiting for delivery OTP confirmation to open your 48-hour claim window.
            </p>
          )}

          {order.canRaiseClaim && (
            <button
              type="button"
              onClick={handleRaiseClaim}
              className="w-full rounded-xl border border-amber-600 py-3 text-sm font-semibold text-amber-300 hover:bg-amber-950/30"
            >
              Raise return / claim
            </button>
          )}

          {order.canReportPackageContact && (
            <button
              type="button"
              disabled={acting}
              onClick={handleReportPackageContact}
              className="w-full rounded-xl border border-red-700/40 py-3 text-sm font-semibold text-red-300 hover:bg-red-950/25 disabled:opacity-50"
            >
              Report contact info in package
            </button>
          )}

          {order.packageContactReported && (
            <p className="text-center text-xs text-emerald-400">
              Package contact report submitted for this order.
            </p>
          )}

          {order.canUseOrderChat ? (
            <button
              type="button"
              onClick={handleMessageSeller}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-600 py-3 text-sm font-semibold text-slate-300 hover:bg-slate-800"
            >
              <FaComments /> Message seller
            </button>
          ) : (
            <p className="text-center text-xs text-slate-500 py-2">
              {currentState === 'PENDING'
                ? 'Chat opens once the seller accepts your order.'
                : 'Chat closed — this order is complete.'}
            </p>
          )}
        </section>

        {process.env.NODE_ENV !== 'production' && (
          <p className="mt-6 text-center text-xs text-slate-600">
            Dev: add ?mockState=DISPATCHED to URL to preview states
          </p>
        )}

        <button
          type="button"
          onClick={() => navigate('/myorder')}
          className="mt-4 w-full rounded-xl border border-slate-600 py-3 text-sm text-slate-400 hover:text-slate-200"
        >
          View all orders
        </button>
      </div>

      <CancelOrderModal
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          setCancelFeeRequired(false);
        }}
        order={order}
        onConfirm={handleCancel}
        submitting={acting}
        cancelFeeRequired={cancelFeeRequired}
      />

      {showChat && (
        <OrderChatPanel orderId={orderId} onClose={() => setShowChat(false)} />
      )}
    </div>
  );
}
